/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { ImportResponse, StateStep } from '@touchplan/common';
import { genAnalyticsBoilerplate } from './AnalyticsDecorators';
import { analyticsService } from './AnalyticsService';

export enum AnalyticsPlanState {
	Archived = 'Archived',
	Active = 'Active',
}

export enum AnalyticsExcludeFromDashboard {
	ExcludeInAnalytics = 'ExcludeInAnalytics',
	IncludeInAnalytics = 'IncludeInAnalytics',
}

export class AnalyticsEventsService {
	//to declare additional events of the standard format the below can be used:
	//@genAnalyticsBoilerplate(["argument1"]) eventNameInCamelCase(argument1: type1){ return this.getGlobals(['projectId']); }

	//START - legacy support section
	public login(userId: string) {
		analyticsService.track('Log in', { userId: userId });
	}

	public errorClient(customProperties: object) {
		analyticsService.track('error-client', customProperties);
	}

	public fbdbRequest(eventNameSegment: string, customProperties: object) {
		analyticsService.track('fbdb request ' + eventNameSegment, customProperties);
	}

	//END - legacy support section
	@genAnalyticsBoilerplate([]) public logOut() {}
	@genAnalyticsBoilerplate(['planId', 'planName', 'date']) public activeLineSetViaCalendar(
		planId: string,
		planName: string,
		date: string
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['ticketId', 'planId', 'actionType', 'isFloating']) public addedAConstraint(
		ticketId: string,
		planId: string,
		actionType: string,
		isFloating: boolean
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['ticketId', 'planId', 'actionType', 'isFloating']) public addedAMilestone(
		ticketId: string,
		planId: string,
		actionType: string,
		isFloatin: boolean
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['ticketId', 'planId', 'actionType', 'isFloating']) public addedATicket(
		ticketId: string,
		planId: string,
		actionType: string,
		isFloating: boolean
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['projectMemberId', 'invitedEmail']) public addProjectMemberUserExists(
		projectMemberId: string,
		invitedEmail: string
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['invitedEmail']) public addProjectMemberUserDoesNotExist(invitedEmail: string) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['myTicketsCount', 'ticketCount', 'planId']) public clickedOnlyViewMyTickets(
		myTicketsCount: number,
		ticketCount: number,
		planId: string
	) {
		return analyticsService.getGlobals(['projectId']);
	}

	//the plus/ minus buttons
	@genAnalyticsBoilerplate([]) public clickedZoomButton() {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['oldPlanId', 'newPlanId']) public copiedAPlan(
		oldPlanId: string,
		newPlanId: string
	) {
		return analyticsService.getGlobals(['projectId', 'projectName']);
	}

	//user created
	@genAnalyticsBoilerplate(['userId', 'email', 'projects']) public created(
		userId: string,
		email: string,
		projects: any
	) {}
	@genAnalyticsBoilerplate(['projectId', 'name', 'projectLocation']) public createdAProject(
		projectId: string,
		name: string,
		projectLocation: any
	) {}
	@genAnalyticsBoilerplate(['locationId', 'planId', 'locationName', 'background']) public createdALocation(
		locationId: string,
		planId: string,
		locationName: string,
		background?: string
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['planId', 'planName']) public createdAPlan(planId: string, planName: string) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['roleId', 'roleName']) public createdARole(roleId: string, roleName: string) {
		return analyticsService.getGlobals(['projectId', 'projectName', 'state']);
	}
	@genAnalyticsBoilerplate([]) public dateViolationBlocked() {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate([]) public dateViolationNagConfirm() {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['locationId', 'planId', 'locationName']) public deletedALocation(
		locationId: string,
		planId: string,
		locationName?: string
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['planId', 'planName']) public deletedAPlan(planId: string, planName: string) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate([]) public deletedAProject() {
		return analyticsService.getGlobals(['projectId', 'projectName']);
	}
	@genAnalyticsBoilerplate(['projectMemberId', 'userId']) public deletedAProjectMember(
		projectMemberId: string,
		userId: string
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['roleId', 'roleName']) public deletedARole(roleId: string, roleName: string) {
		return analyticsService.getGlobals(['projectId', 'projectName', 'state']);
	}
	@genAnalyticsBoilerplate(['ticketId', 'planId', 'actionType', 'isFloating', 'type']) public deletedATicket(
		ticketId: string,
		planId: string,
		actionType: string,
		isFloating: boolean,
		type: string
	) {
		return analyticsService.getGlobals(['projectId', 'projectName']);
	}
	@genAnalyticsBoilerplate(['activeLineDate', 'columnMovedTo', 'columnFinish', 'columnsMoved', 'planId'])
	public draggedActiveLine(
		activeLineDate: string,
		columnMovedTo: any,
		columnFinish: any,
		columnsMoved: number,
		planId: string
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['ticketId', 'planId', 'isFloating']) public duplicatedATicket(
		ticketId: string,
		planId: string,
		isFloating: boolean
	) {
		return analyticsService.getGlobals(['projectId', 'projectName']);
	}
	@genAnalyticsBoilerplate(['ticketId', 'planId', 'planLocked', 'promisedFinish', 'isFloating'])
	public editedATicket(
		ticketId: string,
		planId: string,
		planLocked: boolean,
		promisedFinish: string,
		isFloating: boolean
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['planId']) public exitPlan(planId: string) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['projectName']) public freeTrialActivated(projectName: string) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['type', 'requestId', 'startDate', 'endDate', 'roles', 'plans'])
	public generatedReport(
		type: string,
		requestId: string,
		startDate: string,
		endDate: string,
		roles: any,
		plans: any
	) {
		return analyticsService.getGlobals(['projectId', 'state']);
	}
	@genAnalyticsBoilerplate(['ticketId', 'locationId', 'planId']) public locationSetOnTicket(
		ticketId: string,
		locationId: string,
		planId?: string
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['planId']) public lockPlan(planId: string) {
		return analyticsService.getGlobals(['projectId', 'state']);
	}
	@genAnalyticsBoilerplate(['name', 'date', 'planId']) public milestoneChanged(
		name: string,
		date: string,
		planId: string
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['projectMemberId', 'isAdmin', 'planId']) public missingRoleOnPlanLoad(
		projectMemberId: string,
		isAdmin: boolean,
		planId: string
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate([
		'count',
		'planId',
		'withinMyTickets',
		'toMyTickets',
		'fromMyTickets',
		'crossedLine',
	])
	public movedTickets(
		count: number,
		planId: string,
		withinMyTickets: boolean,
		toMyTickets: boolean,
		fromMyTickets: boolean,
		crossedLine: boolean
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['count', 'planId']) public multiselectDelete(count: number, planId: string) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['count', 'planId']) public multiselectDoubleTap(count: number, planId: string) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['count', 'planId']) public multiselectDuplicate(count: number, planId: string) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['count', 'planId']) public multiselectRegion(count: number, planId: string) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['count', 'planId']) public multiselectTapPlanToCancel(
		count: number,
		planId: string
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['planId', 'ticketCount']) public openedEditAll(
		planId: string,
		ticketCount: number
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['planId', 'ticketCount']) public openedEditCycle(
		planId: string,
		ticketCount: number
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate([]) public openedReportsPanel() {
		return analyticsService.getGlobals(['projectId', 'projectName']);
	}
	@genAnalyticsBoilerplate([]) public partialUserCreation() {}
	@genAnalyticsBoilerplate(['projectId', 'message']) public planDataBadData(
		projectId: string,
		message: string
	) {}

	//planDataCloseSyncForm
	@genAnalyticsBoilerplate(['projectId']) public planDataSyncClosed(projectId: string) {}
	@genAnalyticsBoilerplate(['projectId']) public planDataSyncFromText(projectId: string) {}

	//planDataOpenSyncForm
	@genAnalyticsBoilerplate(['projectId']) public planDataSyncOpened(projectId: string) {}
	@genAnalyticsBoilerplate([]) public positionViolationBlocked() {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate([]) public positionViolationNagConfirm() {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['projectId', 'planId']) public procoreDeleteLinkToRfi(
		projectId: string,
		planId: string
	) {}

	//procoreGetRFIPanelEdit
	@genAnalyticsBoilerplate(['projectId', 'planId']) public procoreGetRfiPanelEdit(
		projectId: string,
		planId: string
	) {}

	//procoreGetRFITicketEdit
	@genAnalyticsBoilerplate(['projectId', 'planId']) public procoreGetRfiTicketEdit(
		projectId: string,
		planId: string
	) {}

	//procoreLinkExisitingRFI
	@genAnalyticsBoilerplate(['projectId', 'planId']) public procoreLinkExistingRfi(
		projectId: string,
		planId: string
	) {}

	//procoreLinkNewRFI
	@genAnalyticsBoilerplate(['projectId', 'planId']) public procoreLinkNewRfi(
		projectId: string,
		planId: string
	) {}
	@genAnalyticsBoilerplate(['projectId']) public procoreLogin(projectId: string) {}

	//procoreViewRFIInProcore
	@genAnalyticsBoilerplate(['projectId', 'planId']) public procoreViewRfiInProcore(
		projectId: string,
		planId: string
	) {}
	@genAnalyticsBoilerplate(['email']) public projectMemberInviteDeleted(email: string) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate([]) public projectViewExpanded() {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate([]) public projectViewJumpToNewPlan() {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate([]) public projectViewOpened() {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate([]) public projectViewSeeAllClicked() {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate([]) public projectViewSettingsClicked() {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate([]) public promiseIntersectChanged() {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['planId']) public promisePeriodDragged(planId: string) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['planId', 'planName', 'promiseId', 'ticketCount']) public promisePeriodLocked(
		planId: string,
		planName: string,
		promiseId: string,
		ticketCount: number
	) {
		return analyticsService.getGlobals(['projectId', 'projectType']);
	}
	@genAnalyticsBoilerplate(['ticketId', 'planId']) public promisePinAddedToTicket(
		ticketId: string,
		planId: string
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['variance']) public promisePinClickedOnPlan(variance: any) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['ticketId', 'planId']) public promisePinRemovedFromTicket(
		ticketId: string,
		planId: string
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate([]) public promiseSettingsOpened() {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['accessCode']) public rallyPointAccessCodeEnded(accessCode: string) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['accessCode']) public rallyPointAccessCodeGenerated(accessCode: string) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate([]) public rallyPointHideDetailsClicked() {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['sortType']) public rallyPointListSorted(sortType: any) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate([]) public rallyPointOpened() {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate([]) public rallyPointShowDetailsClicked() {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['type']) public requestCsv(type: string) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['email', 'sendingUserId']) public resendInvite(
		email: string,
		sendingUserId: string
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['email']) public resetPassword(email: string) {}
	@genAnalyticsBoilerplate(['roleId', 'name', 'style']) public roleColorChanged(
		roleId: string,
		name: string,
		style: string
	) {
		return analyticsService.getGlobals(['projectId', 'state']);
	}
	@genAnalyticsBoilerplate(['searchToggleState']) public searchBarOpened(searchToggleState: any) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['menuAlreadyOpen', 'ticketId', 'planId']) public selectedDependencyTarget(
		menuAlreadyOpen: boolean,
		ticketId: string,
		planId: string
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate([]) public setProjectImage() {
		return analyticsService.getGlobals(['projectId', 'projectName']);
	}
	@genAnalyticsBoilerplate(['attachmentType']) public setProjectAttachment(attachmentType: any) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['snapshotId']) public snapshotCreated(snapshotId: string) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['planId']) public snapshotLogOpened(planId: string) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['snapshotId', 'name', 'type', 'planId']) public snapshotRestored(
		snapshotId: string,
		name: string,
		type: any,
		planId: string
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['snapshotId', 'name']) public snapshotSaved(snapshotId: string, name: string) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['on']) public toggledShowAllDependencies(on: boolean) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['on']) public toggledShowPinHistory(on: boolean) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['on']) public toggledEnableTicketOverlap(on: boolean) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['planId']) public unlockPlan(planId: string) {
		return analyticsService.getGlobals(['projectId', 'state']);
	}
	@genAnalyticsBoilerplate(['planId', 'ticketId']) public updateStatusButtonClicked(
		planId: string,
		ticketId: string
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['planId', 'ticketId', 'varianceId']) public varianceClearedByMovingTicket(
		planId: string,
		ticketId: string,
		varianceId: string
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['planId', 'ticketId', 'varianceId']) public varianceSetByMovingTicket(
		planId: string,
		ticketId: string,
		varianceId: string
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['planId', 'planName']) public viewedAPlan(planId: string, planName: string) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['name']) public viewedAProject(name: string) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['ticketId', 'planId', 'planLocked', 'isFloating']) public viewedATicket(
		ticketId: string,
		planId: string,
		planLocked: boolean,
		isFloating: boolean
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate([]) public wentToProfile() {
		return analyticsService.getGlobals(['state']);
	}
	@genAnalyticsBoilerplate(['planId', 'planName', 'projectName']) public zoomToFit(
		planId: string,
		planName: string,
		projectName: string
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['planId', 'planName', 'projectName']) public zoomToPrint(
		planId: string,
		planName: string,
		projectName: string
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['planId', 'filterValue']) public showCrewSizes(
		planId: string,
		filterValue: string
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['planId']) public hideCrewSizes(planId: string) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['planId', 'filterValue']) public setCrewSizesFilter(
		planId: string,
		filterValue: string
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['projectId']) public pokeProjectPresence(projectId: string) {
		return analyticsService.getGlobals(['projectId']);
	}
	// Release-37
	@genAnalyticsBoilerplate([]) public setActualStartDate() {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate([]) public setActualEndDate() {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate([]) public lockEndDate() {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate([]) public lockDuration() {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['phase']) public togglePlanActual(phase: string) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate([]) public revertToPlanned() {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate([]) public clickFocusTicket() {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate([]) public clickTicketMenu() {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['planId', 'ticketId']) public clickFastUpdate(planId: string, ticketId: string) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['planId', 'ticketId']) public fastCompleteAsPlanned(
		planId: string,
		ticketId: string
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['planId', 'ticketId']) public fastCompleteWithChanges(
		planId: string,
		ticketId: string
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate([]) public addedSwimlane() {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['userId']) public clickedSupport(userId: string) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['userId']) public clickedSupportDocuments(userId: string) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['userId']) public clickedTouchplanGoSupportDocuments(userId: string) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['userId']) public clickedFaq(userId: string) {
		return analyticsService.getGlobals(['projectId']);
	}

	//meeting popup-
	@genAnalyticsBoilerplate(['planId']) public openedVideoMeeting(planId: string) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['planId']) public createdVideoMeeting(planId: string) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['planId']) public joinedVideoMeeting(planId: string) {
		return analyticsService.getGlobals(['projectId']);
	}

	//fbdbRequests
	//utils.analyticsService.track('fbdb request ' + event, obj);
	@genAnalyticsBoilerplate(['requestName', 'requestId', 'date']) public fbdbRequestStarted(
		requestName: string,
		requestId: string,
		date: Date
	) {}
	@genAnalyticsBoilerplate(['requestName', 'requestId', 'date', 'interval']) public fbdbRequestCompleted(
		requestName: string,
		requestId: string,
		date: Date,
		interval: number
	) {}
	@genAnalyticsBoilerplate(['requestName', 'requestId', 'date', 'interval', 'error'])
	public fbdbRequestHitError(
		requestName: string,
		requestId: string,
		date: Date,
		interval: number,
		error: string
	) {}
	@genAnalyticsBoilerplate(['requestName', 'requestId', 'date', 'interval']) public fbdbRequestCancelled(
		requestName: string,
		requestId: string,
		date: Date,
		interval: number
	) {}

	// dashboard
	@genAnalyticsBoilerplate(['dashboardId', 'dashboardName', 'memberId', 'organization'])
	public openEmbeddedDashboard(
		dashboardId: string,
		dashboardName: string,
		memberId: string,
		organization: string
	) {}

	// custom fields
	@genAnalyticsBoilerplate(['planId']) public defineCustomField(
		planId: string,
		customFieldId: string,
		label: string
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['planId']) public editCustomField(
		planId: string,
		customFieldId: string,
		label: string
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['planId']) public showHideCustomField(
		planId: string,
		customFieldId: string,
		label: string,
		visible: boolean
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['planId', 'ticketId', 'customFieldId']) public followHyperlink(
		planId: string,
		ticketId: string,
		customFieldId: string
	) {
		return analyticsService.getGlobals(['projectId']);
	}

	// external schedule
	@genAnalyticsBoilerplate([]) public exportMasterSchedule() {
		return analyticsService.getGlobals(['projectId']);
	}

	// file attach
	@genAnalyticsBoilerplate(['planId', 'fileType']) public fileViewed(planId: string, fileType: string) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['planId', 'fileType']) public fileAdded(planId: string, fileType: string) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['planId', 'fileType']) public fileDeleted(planId: string, fileType: string) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['planId', 'fileType']) public fileCaptionEdit(planId: string, fileType: string) {
		return analyticsService.getGlobals(['projectId']);
	}

	// ticket import
	@genAnalyticsBoilerplate(['operation', 'result', 'planId', 'importMetadata'])
	public ticketImportUpdateExport(
		operation: string,
		result: ImportResponse,
		planId?: string,
		importMetadata?: string
	) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['operation']) public ticketTemplateDownloaded(operation: string) {
		return analyticsService.getGlobals(['projectId']);
	}
	@genAnalyticsBoilerplate(['operation']) public importUpdateErrorsDownloaded(operation: string) {
		return analyticsService.getGlobals(['projectId']);
	}

	// hover over
	@genAnalyticsBoilerplate(['enabled']) public setHoverVisibility(enabled: boolean) {
		return analyticsService.getGlobals(['projectId']);
	}

	@genAnalyticsBoilerplate([]) public setHoverConfig() {
		return analyticsService.getGlobals(['projectId']);
	}

	// Ticket Import v2
	@genAnalyticsBoilerplate(['planId', 'dataSourceId']) public dataSourceEdited(
		planId: string,
		dataSourceId: string
	) {
		return analyticsService.getGlobals(['projectId']);
	}

	@genAnalyticsBoilerplate(['planId', 'stepId']) public ticketImportStep(planId: string, stepId: StateStep) {
		return analyticsService.getGlobals(['projectId']);
	}

	// Start work
	@genAnalyticsBoilerplate(['planId', 'ticketId']) public startWork(planId: string, ticketId: string) {
		return analyticsService.getGlobals(['projectId']);
	}

	@genAnalyticsBoilerplate(['planId', 'ticketId']) public fastStartWork(planId: string, ticketId: string) {
		return analyticsService.getGlobals(['projectId']);
	}

	// Plan Archive Events

	// Archive Plan Report Run
	@genAnalyticsBoilerplate(['planIds', 'archivedPlanIds']) public archivedReportRun(
		planIds?: string[],
		archivedPlanIds?: string[]
	) {
		return analyticsService.getGlobals(['projectId']);
	}

	// Plan State Saved
	@genAnalyticsBoilerplate(['planId', 'planState', 'analyticsAccess']) public planStateSaved(
		planId: string,
		planState?: AnalyticsPlanState,
		analyticsAccess?: AnalyticsExcludeFromDashboard
	) {
		return analyticsService.getGlobals(['projectId']);
	}

	// Organization Schedule Disclaimer
	@genAnalyticsBoilerplate(['organizationId', 'planId', 'userId', 'timestamp'])
	public organizationScheduleDisclaimerAccepted(
		organizationId: string,
		planId: string,
		userId: string,
		timestamp: string
	) {
		return analyticsService.getGlobals(['projectId']);
	}
}

export const analyticsEventsService = new AnalyticsEventsService();
